// Las dos puertas del alta en bloque por carpetas
// (ADR-2026-10-16-subir-varios-con-zip-y-carpetas): un .zip o una carpeta soltada
// en escritorio. Las dos terminan en `organizarPorCarpetas`, que tiene la regla.

import {
  limitarPdfs,
  limitarProductos,
  mapearLote,
  organizarConPdfs,
  repararNombreDeZip,
  type EntradaDeLote,
  type LotePorCarpetas,
} from "./lotePorCarpetas";
import { extraerDelZip, indiceDelZip, ZipNoSoportado, type EntradaDelZip } from "./zipPorPartes";

/** Una foto descomprimida: el mismo tope que una foto suelta (`BULK_MAX_FILE_SIZE`). */
export const MAX_FOTO_DESCOMPRIMIDA = 100 * 1024 * 1024;
/**
 * Todo lo que se saca de un .zip, que queda en memoria hasta subirse. El .zip en sí ya no tiene
 * tope: se lee por partes (ADR-2026-11-03-el-zip-se-lee-por-partes), así que uno de varios GB
 * ocupa sólo lo que entra en la carga. Este tope frena un .zip chico que se expande a mucho más
 * (review Greptile #184) y una carga que no entra en la memoria de una computadora común: 100
 * productos × 5 fotos de cámara de ~4 MB son 2 GB.
 */
export const MAX_TOTAL_DESCOMPRIMIDO = 2 * 1024 * 1024 * 1024;

/** Error con un mensaje que se le puede mostrar tal cual al usuario. */
export class ErrorDeLote extends Error {}

export const esZip = (file: File): boolean =>
  /\.zip$/i.test(file.name) || /^application\/(x-)?zip(-compressed)?$/.test(file.type);

const MIME_POR_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  avif: "image/avif",
  pdf: "application/pdf",
};

const nombreBase = (ruta: string) => ruta.slice(ruta.replace(/\\/g, "/").lastIndexOf("/") + 1);

/** Las fotos (y los PDF) de un .zip no traen tipo: sin él, el preview y el upload no
 *  saben qué son. Se deduce de la extensión, la misma que filtró `organizarPorCarpetas`. */
const mimeDe = (ruta: string) =>
  MIME_POR_EXTENSION[ruta.slice(ruta.lastIndexOf(".") + 1).toLowerCase()] ?? "";

/**
 * Lee un .zip y arma sus productos. Dos pasadas: la primera sólo lee el índice del .zip (los
 * nombres y tamaños, sin descomprimir nada); la segunda saca, de a uno, nada más los archivos
 * que van a entrar (hasta `maxProductos` y 5 fotos por carpeta). Ninguna carga el .zip entero
 * (ADR-2026-11-03-el-zip-se-lee-por-partes): un .zip con 300 carpetas no descomprime 1500 fotos
 * para usar 100. El PDF de la carpeta de un producto va en su `pdf`
 * (ADR-2026-11-03-la-carpeta-junta-fotos-y-pdf); los demás vuelven en `pdfs` y el hook lee cada
 * uno como un PDF suelto (ADR-2026-11-03-el-zip-trae-pdfs).
 */
export async function leerZipDeProductos(
  zip: File,
  maxProductos: number,
  // Inyectables sólo para los tests (armar un .zip de 100 MB en un test no tiene sentido).
  topes = { foto: MAX_FOTO_DESCOMPRIMIDA, total: MAX_TOTAL_DESCOMPRIMIDO },
): Promise<{ lote: LotePorCarpetas<File>; pdfs: File[]; omitidos: number; pesadas: number }> {
  const noAbre = new ErrorDeLote(
    `No pudimos abrir "${zip.name}". Si tiene contraseña, sacásela; si no, volvé a comprimirlo y probá de nuevo.`,
  );
  let indice: EntradaDelZip[];
  try {
    indice = await indiceDelZip(zip);
  } catch {
    throw noAbre;
  }
  // Por nombre crudo: si el .zip repite un nombre, gana el último (como al descomprimirlo entero).
  const porNombre = new Map(indice.map((e) => [e.nombre, e]));
  const tamano = new Map(indice.map((e) => [e.nombre, e.tamano]));
  const nombres = [...porNombre.keys()];

  // El dato de cada archivo es su nombre crudo: es la clave del índice.
  const organizado = organizarConPdfs(
    nombres.map((n) => ({ ruta: repararNombreDeZip(n), dato: n })),
  );
  const limitado = limitarProductos(organizado.lote, maxProductos);
  const { lote } = limitado;
  const limitados = limitarPdfs(organizado.pdfs, maxProductos, lote.carpetas.length);
  const omitidos = limitado.omitidos + limitados.omitidos;
  // Los PDF que se descomprimen: los sueltos que entran y el de cada carpeta que entró.
  const pdfs = new Set([
    ...limitados.pdfs,
    ...lote.carpetas.flatMap((c) => (c.pdf === undefined ? [] : [c.pdf])),
  ]);
  const elegidos = new Set(
    [...lote.carpetas.flatMap((c) => c.archivos), ...lote.sueltas].filter(
      (n) => (tamano.get(n) ?? 0) <= topes.foto,
    ),
  );
  const pesadas =
    lote.carpetas.reduce((n, c) => n + c.archivos.length, 0) + lote.sueltas.length - elegidos.size;
  if (elegidos.size === 0 && limitados.pdfs.length === 0)
    return { lote: mapearLote<string, File>(lote, () => null), pdfs: [], omitidos, pesadas };
  // Un PDF no tiene el tope de una foto (el lector del PDF avisa si es muy grande), pero
  // sí cuenta para el total.
  let total = 0;
  for (const n of [...elegidos, ...pdfs]) total += tamano.get(n) ?? 0;
  if (total > topes.total) {
    throw new ErrorDeLote(
      `Los archivos de "${zip.name}" ocupan más de ${Math.round(topes.total / 1024 / 1024)} MB descomprimidos. Partilo en varios .zip más chicos.`,
    );
  }

  // De a uno y en el orden del .zip (leer en orden es lo más rápido en disco).
  const aSacar = [...elegidos, ...pdfs]
    .map((n) => porNombre.get(n))
    .filter((e): e is EntradaDelZip => e !== undefined)
    .sort((a, b) => a.offset - b.offset);
  // Cada uno pasa a File apenas sale: los bytes sueltos no se quedan en memoria al lado de la copia.
  const archivos = new Map<string, File>();
  for (const entrada of aSacar) {
    try {
      const nombre = repararNombreDeZip(entrada.nombre);
      const contenido = await extraerDelZip(zip, entrada);
      archivos.set(entrada.nombre, new File([contenido], nombreBase(nombre), { type: mimeDe(nombre) }));
    } catch (err) {
      if (err instanceof ZipNoSoportado && err.motivo === "metodo") {
        throw new ErrorDeLote(
          `"${zip.name}" está comprimido de una forma que no podemos abrir. Volvé a comprimirlo con la opción de siempre de tu computadora ("Comprimir" en Mac, "Enviar a carpeta comprimida" en Windows).`,
        );
      }
      throw noAbre;
    }
  }
  const comoArchivo = (crudo: string): File | null => archivos.get(crudo) ?? null;
  return {
    lote: mapearLote(lote, comoArchivo),
    pdfs: limitados.pdfs.map(comoArchivo).filter((f): f is File => f !== null),
    omitidos,
    pesadas,
  };
}

// ── Carpetas soltadas ─────────────────────────────────────────────────────

const leerDirectorio = (dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> =>
  new Promise((resolve, reject) => {
    const lector = dir.createReader();
    const todas: FileSystemEntry[] = [];
    // `readEntries` devuelve de a tandas (Chrome, de a 100): hay que llamarlo hasta
    // que venga vacío o se pierden archivos de las carpetas grandes.
    const tanda = () =>
      lector.readEntries((es) => {
        if (es.length === 0) resolve(todas);
        else {
          todas.push(...es);
          tanda();
        }
      }, reject);
    tanda();
  });

const leerArchivo = (entrada: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve, reject) => entrada.file(resolve, reject));

async function recorrer(entrada: FileSystemEntry, salida: EntradaDeLote<File>[]) {
  // Ocultos y `__MACOSX` ni se recorren; `organizarPorCarpetas` igual los filtra.
  if (entrada.name.startsWith(".") || entrada.name === "__MACOSX") return;
  if (entrada.isFile) {
    try {
      salida.push({
        ruta: entrada.fullPath,
        dato: await leerArchivo(entrada as FileSystemFileEntry),
      });
    } catch {
      // Un archivo que el sistema no deja leer se saltea; el resto sigue.
    }
    return;
  }
  if (entrada.isDirectory) {
    for (const hija of await leerDirectorio(entrada as FileSystemDirectoryEntry)) {
      await recorrer(hija, salida);
    }
  }
}

/**
 * Si lo que se soltó trae al menos una carpeta, devuelve la función que la lee;
 * si son sólo archivos, null (el camino de siempre con `dataTransfer.files`).
 *
 * Se llama SINCRÓNICAMENTE dentro del `onDrop`: los `DataTransferItem` dejan de
 * valer después del primer `await`, por eso se capturan las entradas acá y la
 * lectura (asíncrona) queda para después.
 */
export function capturarCarpetasSoltadas(
  dt: DataTransfer,
): (() => Promise<EntradaDeLote<File>[]>) | null {
  const entradas = Array.from(dt.items ?? [])
    .filter((i) => i.kind === "file")
    .map((i) => i.webkitGetAsEntry?.() ?? null)
    .filter((e): e is FileSystemEntry => e !== null);
  if (!entradas.some((e) => e.isDirectory)) return null;
  return async () => {
    const salida: EntradaDeLote<File>[] = [];
    for (const e of entradas) await recorrer(e, salida);
    return salida;
  };
}
